<script setup lang="ts">
import { computed, inject } from 'vue'
import { CarouselDesktopSizes, EMobileSlidesVariants, type ICarousel } from './types'

const carouselProps: ICarousel | undefined = inject('carouselProps')

const mobileSlidesVariantsClasses: Record<EMobileSlidesVariants, string> = {
    [EMobileSlidesVariants.FULLWIDTH]: /* tw */ 'max-md:!basis-full max-md:!max-w-full max-md:px-2',
    [EMobileSlidesVariants.ONE_BIG_CARD]: /* tw */ 'max-md:!basis-10/12 max-md:!max-w-[83.3%] max',
    [EMobileSlidesVariants.ONE_SMALL_CARD]: /* tw */ 'max-md:!basis-8/12 max-md:!max-w-[66.6%]',
    [EMobileSlidesVariants.TWO_CARDS]: /* tw */ 'max-md:!basis-[45.5%] max-md:!max-w-[45.5%]',
    [EMobileSlidesVariants.THREE_CARDS]: /* tw */ 'max-md:!basis-[31%] max-md:!max-w-[31%]',
    [EMobileSlidesVariants.FOUR_CARDS]: /* tw */ 'max-md:!basis-[23.8%] max-md:!max-w-[23.8%]',
    [EMobileSlidesVariants.CUSTOM]: /* tw */ '',
}

const tabletSlidesVariantsClasses: Record<EMobileSlidesVariants, string> = {
    [EMobileSlidesVariants.FULLWIDTH]: /* tw */ 'md:max-lg:!basis-full md:max-lg:!max-w-full md:max-lg:px-3',
    [EMobileSlidesVariants.ONE_BIG_CARD]: /* tw */ 'md:max-lg:!basis-10/12 md:max-lg:!max-w-[83.3%]',
    [EMobileSlidesVariants.ONE_SMALL_CARD]: /* tw */ 'md:max-lg:!basis-8/12 md:max-lg:!max-w-[66.6%]',
    [EMobileSlidesVariants.TWO_CARDS]: /* tw */ 'md:max-lg:!basis-[45.5%] md:max-lg:!max-w-[45.5%]',
    [EMobileSlidesVariants.THREE_CARDS]: /* tw */ 'md:max-lg:!basis-[31%] md:max-lg:!max-w-[31%]',
    [EMobileSlidesVariants.FOUR_CARDS]: /* tw */ 'md:max-lg:!basis-[23.8%] md:max-lg:!max-w-[23.8%]',
    [EMobileSlidesVariants.CUSTOM]: /* tw */ '',
}

const mobileVariantClass = computed(() => {
    if (!carouselProps?.mobileSlidesVariant) {
        return ''
    }

    return mobileSlidesVariantsClasses[carouselProps.mobileSlidesVariant] || ''
})

const tabletVariantClass = computed(() => {
    if (!carouselProps?.tabletSlidesVariant) {
        return ''
    }

    return tabletSlidesVariantsClasses[carouselProps.tabletSlidesVariant] || ''
})

const desktopSlidesWidthStyles = computed(() => {
    if (!carouselProps?.desktopSlidesPerView) {
        return {}
    }

    const slidesPerView = Number(carouselProps.desktopSlidesPerView)

    if (Number.isNaN(slidesPerView) || slidesPerView <= 0) {
        return {}
    }

    const widthPercentage = (100 / slidesPerView).toFixed(2)

    return {
        flexBasis: `${widthPercentage}%`,
        maxWidth: `${widthPercentage}%`,
    }
})
</script>

<template>
    <div
        class="shrink-0 grow-0 select-none px-1 transition-opacity duration-200"
        :class="[
            {
                'lg:px-1': carouselProps?.desktopSize === CarouselDesktopSizes.MEDIUM,
                'lg:px-3': carouselProps?.desktopSize === CarouselDesktopSizes.LARGE,
            },
            mobileVariantClass,
            tabletVariantClass,
        ]"
        :style="desktopSlidesWidthStyles"
    >
        <slot />
    </div>
</template>
